import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Modal, Button, Spinner, Breadcrumb, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Folder as FolderIcon,
  FolderOpen,
  Image as ImageIcon,
  Film,
  FileText,
  File as FileIcon,
  Home,
  Download,
  Maximize2,
  X,
  RotateCcw,
  Grid as GridIcon,
  List as ListIcon,
  ZoomIn,
  Search,
} from "lucide-react";
import "./MediaGallery.css";

/**
 * MediaGallery.jsx — v3 (Modern + Polished)
 *
 * - Accent color support (default #FC981B)
 * - Grid/List views with compact/comfortable density
 * - Blur‑up image loading + error fallback tiles
 * - Hover overlays, two‑line name clamp, refined spacing
 * - Sticky header with search, sort
 */

const ENDPOINT = "https://payinnew.remittpe.com/api/Order/v1.0/getMediaGallery";

const typeFrom = (item) => {
  if (item?.fileType) return item.fileType.toUpperCase();
  const ext = (item?.fileName || "").split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "gif", "bmp", "svg"].includes(ext)) return "IMAGE";
  if (["mp4", "webm", "ogg", "mov", "m4v"].includes(ext)) return "VIDEO";
  if (["pdf", "txt", "doc", "docx", "xls", "xlsx", "csv"].includes(ext)) return "DOCUMENT";
  return "FILE";
};

const prettyName = (str = "") => str.replaceAll("_", " ");

const Badge = ({ type }) => (
  <span className={`mg-badge mg-${type.toLowerCase()}`}>{type}</span>
);

const FolderCard = ({ name, onOpen, view }) => (
  <button className={`mg-card mg-folder ${view === "list" ? "mg-list" : ""}`} onClick={onOpen} aria-label={`Open ${name}`}>
    <div className="mg-folder-thumb">
      <div className="mg-folder-tab" />
      <div className="mg-folder-body"><FolderIcon size={36} /></div>
    </div>
    <div className="mg-meta">
      <div className="mg-title clamp-2" title={prettyName(name)}>{prettyName(name)}</div>
      <div className="mg-sub">Folder</div>
    </div>
    <Badge type="FOLDER" />
  </button>
);

const ThumbImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return failed ? (
    <div className="mg-file-icon"><ImageIcon size={34} /></div>
  ) : (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`mg-img ${loaded ? "is-loaded" : ""}`}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
    />
  );
};

const FileCard = ({ item, onOpen, view }) => {
  const type = typeFrom(item);
  const icon =
    type === "IMAGE" ? <ImageIcon size={34} /> :
    type === "VIDEO" ? <Film size={34} /> :
    type === "DOCUMENT" ? <FileText size={34} /> : <FileIcon size={34} />;

  const thumb = type === "IMAGE" && item.fileUrl
    ? <ThumbImage src={item.fileUrl} alt={item.fileName} />
    : <div className="mg-file-icon">{icon}</div>;

  const card = (
    <button className={`mg-card mg-file ${view === "list" ? "mg-list" : ""}`} onClick={onOpen} aria-label={`Preview ${item.fileName}`}>
      <div className="mg-file-thumb">
        {thumb}
        <div className="mg-thumb-overlay"><ZoomIn size={16} /></div>
        <div className={`mg-type-chip mg-${type.toLowerCase()}`}>{type}</div>
      </div>
      <div className="mg-meta">
        <div className="mg-title clamp-2" title={prettyName(item.fileName)}>{prettyName(item.fileName)}</div>
        <div className="mg-sub">{type === "IMAGE" ? "Image" : type === "VIDEO" ? "Video" : type === "DOCUMENT" ? "Document" : "File"}</div>
      </div>
    </button>
  );

  return (
    <OverlayTrigger overlay={<Tooltip>{item.fileName}</Tooltip>} placement="bottom">
      <div>{card}</div>
    </OverlayTrigger>
  );
};

const SkeletonCard = ({ view }) => (
  <div className={`mg-card mg-skeleton ${view === "list" ? "mg-list" : ""}`}>
    <div className="mg-skel-thumb" />
    <div className="mg-meta"><div className="mg-skel-line" /><div className="mg-skel-line short" /></div>
  </div>
);

const EmptyState = ({ text = "No items here yet." }) => (
  <div className="mg-empty">
    <FolderOpen size={52} />
    <p className="mb-0 mt-2">{text}</p>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="mg-empty">
    <X size={52} />
    <p className="mb-2">{message || "Something went wrong."}</p>
    {onRetry && (
      <Button variant="outline-dark" size="sm" onClick={onRetry}>
        <RotateCcw size={16} className="me-1" /> Retry
      </Button>
    )}
  </div>
);

const PreviewModal = ({ show, items, activeIndex, onClose, setActiveIndex }) => {
  const item = items[activeIndex];
  const type = item ? typeFrom(item) : null;

  const go = (dir) => {
    if (!items.length) return;
    setActiveIndex((prev) => (prev + dir + items.length) % items.length);
  };

  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    if (show) window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [show]);

  return (
    <Modal show={show} onHide={onClose} size="xl" centered className="mg-preview-modal">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Maximize2 size={18} /> Preview — {item?.fileName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!item ? (
          <div className="text-center"><Spinner animation="border" /></div>
        ) : (
          <div className="mg-preview-wrap">
            {type === "IMAGE" && (
              <img src={item.fileUrl} alt={item.fileName} className="mg-preview-media" />
            )}
            {type === "VIDEO" && (
              <video className="mg-preview-media" controls src={item.fileUrl} />
            )}
            {type === "DOCUMENT" && (
              <iframe className="mg-preview-doc" title={item.fileName} src={item.fileUrl} />
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div className="d-flex gap-2">
          <Button variant="light" onClick={() => go(-1)} disabled={!items.length}>◀ Prev</Button>
          <Button variant="light" onClick={() => go(1)} disabled={!items.length}>Next ▶</Button>
        </div>
        {item?.fileUrl && (
          <Button variant="dark" as="a" href={item.fileUrl} target="_blank" rel="noreferrer">
            <Download size={16} className="me-1" /> Open / Download
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default function MediaGallery({ userID, rootDir = "gallery", accent = "#FC981B" }) {
  const [stack, setStack] = useState([rootDir]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  // UI state
  const [view, setView] = useState("grid"); // grid | list
  const [density, setDensity] = useState("comfortable"); // comfortable | compact
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name-asc");
  const title = "Gallery"

  // accent → CSS var
  const hostRef = useRef(null);
  useEffect(() => {
    if (hostRef.current) hostRef.current.style.setProperty('--mg-accent-color', accent);
  }, [accent]);

  const currentDir = stack[stack.length - 1];

  const fetchDir = useCallback(async (dir) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(ENDPOINT, { userid: userID, directory: dir });
      if (data?.responseCode !== "00") throw new Error(data?.responseMessage || "API error");
      const list = Array.isArray(data?.response) ? data.response : [];
      const normalized = list.map((it) => ({
        galleryId: it.galleryId,
        fileName: it.fileName,
        fileUrl: it.fileUrl || "",
        fileType: typeFrom(it),
      }));
      setItems(normalized);
    } catch (e) {
      setError(e?.message || "Failed to load directory.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [userID]);

  useEffect(() => { fetchDir(currentDir); }, [currentDir, fetchDir]);

  const isRoot = stack.length === 1;
  const openFolder = (name) => setStack((prev) => [...prev, name]);
  const goToCrumb = (index) => setStack((prev) => prev.slice(0, index + 1));

  const { folders, files } = useMemo(() => {
    const folders = items.filter((i) => i.fileType === "FILE");
    const files = items.filter((i) => i.fileType !== "FILE");
    return { folders, files };
  }, [items]);

  // derived: filtered + sorted
  const matchesQuery = (name) => name.toLowerCase().includes(query.toLowerCase());

  const sorters = {
    "name-asc": (a, b) => a.fileName.localeCompare(b.fileName),
    "name-desc": (a, b) => b.fileName.localeCompare(a.fileName),
    "type": (a, b) => typeFrom(a).localeCompare(typeFrom(b)) || a.fileName.localeCompare(b.fileName),
  };

  const fFolders = useMemo(() => folders.filter(f => matchesQuery(f.fileName)).sort(sorters["name-asc"]), [folders, query]);
  const fFiles = useMemo(() => files.filter(f => matchesQuery(f.fileName)).sort(sorters[sort] || sorters["name-asc"]), [files, query, sort]);

  const openPreviewAt = (indexInFiles) => { setPreviewIndex(indexInFiles); setShowPreview(true); };

  return (
    <div ref={hostRef} className={`media-gallery density-${density}`}>
      {/* Header */}
    <header className="mg-header">
  {/* Row 1: Static Title + quick actions that never wrap weirdly */}
  <div className="mg-header-row">
    <h2 className="mg-title-lg">{title}</h2>
    <div className="mg-quick-actions">
      {/* Keep quick essentials here if you want (optional) */}
      {/* Example: <Button size="sm" variant="dark">New Folder</Button> */}
    </div>
  </div>

  {/* Row 2: Breadcrumbs + Toolbar */}
  <div className="mg-header-row mg-header-row-wrap">
    <div className="mg-breadcrumb-wrap">
      <Home size={18} />
      <Breadcrumb className="mg-breadcrumb">
        {stack.map((seg, idx) => (
          <Breadcrumb.Item
            key={idx}
            active={idx === stack.length - 1}
            onClick={() => idx !== stack.length - 1 && goToCrumb(idx)}
            linkAs="button"
            linkProps={{ className: "mg-crumb-btn" }}
          >
            {idx === 0 ? seg : prettyName(seg)}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>
    </div>

    <div className="mg-toolbar">
      <div className="mg-search">
        <Search size={16} />
        <Form.Control
          size="sm"
          type="search"
          placeholder="Search by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Scrollable controls on small screens to avoid overflow */}
      <div className="mg-controls-scroll">
        <div className="mg-controls">
          <Form.Select
            size="sm"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="name-asc">Sort: Name (A→Z)</option>
            <option value="name-desc">Sort: Name (Z→A)</option>
            <option value="type">Sort: Type</option>
          </Form.Select>

          <div className="btn-group" role="group" aria-label="View">
            <Button
              size="sm"
              className="mg-btn"
              variant={view === "grid" ? "dark" : "outline-dark"}
              onClick={() => setView("grid")}
            >
              <GridIcon size={16} />
            </Button>
            <Button
              size="sm"
              className="mg-btn"
              variant={view === "list" ? "dark" : "outline-dark"}
              onClick={() => setView("list")}
            >
              <ListIcon size={16} />
            </Button>
          </div>

          {/* <Form.Select
            size="sm"
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <option value="comfortable">Comfortable</option>
            <option value="compact">Compact</option>
          </Form.Select> */}

          {!isRoot && (
            <Button
              size="sm"
              className="mg-btn"
              variant="outline-dark"
              onClick={() => goToCrumb(stack.length - 2)}
            >
              ⟵ Back
            </Button>
          )}
          <Button
            size="sm"
            className="mg-btn"
            variant="dark"
            onClick={() => fetchDir(currentDir)}
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  </div>
</header>


      {/* Content */}
      {loading ? (
        <div className={`mg-grid ${view === "list" ? "mg-list-grid" : ""}`}>
          {Array.from({ length: 12 }).map((_, i) => (
            <SkeletonCard key={i} view={view} />
          ))}
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchDir(currentDir)} />
      ) : items.length === 0 ? (
        <EmptyState text="This folder is empty." />
      ) : (
        <>
          {fFolders.length > 0 && (
            <>
              <div className="mg-section-title">Folders</div>
              <div className={`mg-grid ${view === "list" ? "mg-list-grid" : ""}`}>
                {fFolders.map((f) => (
                  <FolderCard key={f.galleryId} name={f.fileName} onOpen={() => openFolder(f.fileName)} view={view} />
                ))}
              </div>
            </>
          )}

          {fFiles.length > 0 && (
            <>
              <div className="mg-section-title">Files</div>
              <div className={`mg-grid ${view === "list" ? "mg-list-grid" : ""}`}>
                {fFiles.map((file, idx) => (
                  <FileCard key={file.galleryId} item={file} onOpen={() => openPreviewAt(idx)} view={view} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Preview */}
      <PreviewModal
        show={showPreview}
        items={fFiles}
        activeIndex={previewIndex}
        onClose={() => setShowPreview(false)}
        setActiveIndex={setPreviewIndex}
      />
    </div>
  );
}
